// src/pages/product/ProductDetail.tsx
//
// ProductDetail
//
// Single-page editor for an existing product.
// - Loads product data and hydrates a local "upsert" form state.
// - Keeps the shared PageHeader (title/breadcrumb) managed at the route-level,
//   and injects only contextual "leading" content (product name/code).
// - Uses global Confirm + System Alert providers for user prompts and feedback.
// - Places destructive action (Delete) in a dedicated Danger Zone section.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import { ProductStatus, RequiredDataType } from '@models/product';
import {
  editProduct,
  ProductSaleType,
  ProductType,
  ProductUpsertRequest,
} from '@apis/product';
import { getStock, StockSupplierTaskType } from '@apis/stock';

import { useProductDetail } from '@queries/products/useProductDetail';
import { useAllStockList } from '@queries/stocks/useAllStockList';
import { useCountryList } from '@queries/countries/useCountryList';

import useAsyncEffect from '@hooks/useAsyncEffect';
import { isNullOrValidJson } from '@utils/common';

import { usePageHeader } from '@components/common/pageHeader.context';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import DangerZoneCard from '@components/common/DangerZoneCard';
import ProductBasicInfoSection from '@components/product/ProductBasicInfoSection';
import ProductSpecificationSection from '@components/product/ProductSpecificationSection';
import ProductSettingsSection from '@components/product/ProductSettingsSection';

type Country = {
  countryId: number;
  englishName: string;
};

/**
 * Local form shape:
 * - Uses a flattened Country[] for coverage editing convenience.
 * - Converted back to API shape (ProductUpsertRequest) on submit by the API layer.
 */
type ProductUpsertForm = Omit<ProductUpsertRequest, 'productCoverages'> & {
  productCoverages: Country[];
};

type StockOption = { value: number; label: string };

const PAGE_MAX_WIDTH = 900 as const;

const ProductDetail = () => {
  const { productId } = useParams();
  const productIdNum = Number(productId);

  const navigate = useNavigate();
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();
  const { setPageHeaderSlots } = usePageHeader();

  // ---------------------------------------------------------------------------
  // Local form state
  // ---------------------------------------------------------------------------

  const [form, setForm] = useState<ProductUpsertForm>({
    productCode: '',
    productSaleType: ProductSaleType.REAL_TIME,
    productType: ProductType.ESIM,
    productName: '',
    description: '',
    price: 0,
    specification: {
      dayCount: 0,
      isUnlimitedData: true,
      isVoiceCallSupported: false,
      dataCapacity: {
        quantity: 0,
        unit: 'GB',
        refreshPeriodUnit: 'NEVER',
        refreshPeriodValue: 0,
      },
      isFup: false,
    },
    productCoverages: [],
    requiredData: { requiredFields: [] },
    productStatus: ProductStatus.ACTIVE,
    apiParameter: undefined,
    stockId: 0,
  });

  // ---------------------------------------------------------------------------
  // Data sources
  // ---------------------------------------------------------------------------

  const { data: productDetailForm } = useProductDetail(productIdNum);
  const { data: stocksData } = useAllStockList();
  const { data: countriesData } = useCountryList();

  const [stockOptionList, setStockOptionList] = useState<StockOption[]>([]);
  const [isApiInputNeeded, setIsApiInputNeeded] = useState(false);

  const allCountries: Country[] = useMemo(() => {
    return (countriesData?.data ?? []).map((c) => ({
      countryId: c.countryId,
      englishName: c.englishName,
    }));
  }, [countriesData]);

  // ---------------------------------------------------------------------------
  // Form update helpers (immutable updates)
  // ---------------------------------------------------------------------------

  /**
   * Updates a top-level field in the form state.
   * Use dedicated helpers for nested objects to keep updates explicit.
   */
  const updateField = useCallback(
    (
      name: keyof ProductUpsertRequest,
      value: string | number | boolean | null | undefined,
    ) => {
      setForm((prev) => ({ ...prev, [name]: value as any }));
    },
    [],
  );

  /** Toggles a RequiredDataType within requiredData.requiredFields. */
  const toggleRequiredField = useCallback(
    (field: RequiredDataType, checked: boolean) => {
      setForm((prev) => ({
        ...prev,
        requiredData: {
          ...prev.requiredData,
          requiredFields: checked
            ? [...prev.requiredData.requiredFields, field]
            : prev.requiredData.requiredFields.filter((r) => r !== field),
        },
      }));
    },
    [],
  );

  /** Updates a root field inside the `specification` object. */
  const updateSpecification = useCallback(
    (
      name: keyof ProductUpsertRequest['specification'],
      value: string | boolean | number | null,
    ) => {
      setForm((prev) => ({
        ...prev,
        specification: { ...prev.specification, [name]: value as any },
      }));
    },
    [],
  );

  /** Updates a nested field inside `specification.dataCapacity`. */
  const updateDataCapacity = useCallback(
    (
      name: keyof ProductUpsertRequest['specification']['dataCapacity'],
      value: number | string,
    ) => {
      setForm((prev) => ({
        ...prev,
        specification: {
          ...prev.specification,
          dataCapacity: {
            ...prev.specification.dataCapacity,
            [name]: value as any,
          },
        },
      }));
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Coverage helpers
  // ---------------------------------------------------------------------------

  const addCoverage = useCallback((country: Country) => {
    if (!country?.countryId) return;

    setForm((prev) => {
      const exists = prev.productCoverages.some(
        (c) => c.countryId === country.countryId,
      );
      if (exists) return prev;

      return { ...prev, productCoverages: [...prev.productCoverages, country] };
    });
  }, []);

  const deleteCoverage = useCallback((countryId: number) => {
    setForm((prev) => ({
      ...prev,
      productCoverages: prev.productCoverages.filter((c) => c.countryId !== countryId),
    }));
  }, []);

  // ---------------------------------------------------------------------------
  // Page actions
  // ---------------------------------------------------------------------------

  const handleCancel = useCallback(async () => {
    const ok = await confirm({
      priority: 'info',
      title: 'Cancel changes?',
      description: 'Your unsaved changes will be lost.',
      confirmText: 'Leave',
      cancelText: 'Stay',
      autoFocusConfirm: false,
    });

    if (ok) navigate(-1);
  }, [confirm, navigate]);

  const handleSave = useCallback(async () => {
    try {
      // Keep client validation consistent with existing behavior.
      if (!isNullOrValidJson('apiParameter', form.apiParameter)) return;

      await editProduct(productIdNum, {
        ...form,
        stockId: form.stockId,
      });

      systemAlert.success('Product edited successfully');
    } catch {
      systemAlert.error('Failed to save');
    }
  }, [form, productIdNum, systemAlert]);

  const handleDelete = useCallback(async () => {
    const ok = await confirm({
      priority: 'danger',
      title: 'Delete Product',
      description: 'Permanently removes this product. This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;

    try {
      await editProduct(productIdNum, {
        ...form,
        stockId: form.stockId,
        isDeleted: true,
      });

      systemAlert.success('Product deleted successfully');
      navigate('/products');
    } catch {
      systemAlert.error('Failed to delete');
    }
  }, [confirm, form, navigate, productIdNum, systemAlert]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  /** Build Stock select options (sorted by stock code). */
  useAsyncEffect(async () => {
    if (!stocksData) return;

    const sorted: StockOption[] = [...stocksData.data]
      .map((s) => ({ label: s.stockCode, value: s.stockId }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setStockOptionList(sorted);
  }, [stocksData]);

  /** Detect if the selected stock requires API parameter input. */
  useAsyncEffect(async () => {
    if (!form.stockId) {
      setIsApiInputNeeded(false);
      return;
    }

    const { data } = await getStock(form.stockId);
    const stock = data?.[0];
    if (!stock) return;

    setIsApiInputNeeded(stock.supplierTaskType === StockSupplierTaskType.API);
  }, [form.stockId]);

  /** Hydrate the local form from the fetched product detail. */
  useEffect(() => {
    const base = productDetailForm?.data?.[0];
    if (!base || !productIdNum) return;

    const coverages: Country[] =
      base.productCoverages?.map((c) => ({
        countryId: c.country.countryId,
        englishName: c.country.englishName,
      })) ?? [];

    setForm({
      ...base,
      apiParameter: base.apiParameter ? JSON.stringify(base.apiParameter) : undefined,
      stockId: base.stock.stockId,
      productCoverages: coverages,
    });
  }, [productDetailForm, productIdNum]);

  /**
   * Provide contextual "leading" content for the shared page header.
   * The header component itself owns the main title/breadcrumb layout.
   */
  const headerLeading = useMemo(() => {
    const title = form.productName || 'Product detail';
    const description = form.productCode ? form.productCode : undefined;

    return { title, description };
  }, [form.productName, form.productCode]);

  useEffect(() => {
    setPageHeaderSlots({
      leading: headerLeading,
      actions: [], // keep the header action area empty for this page
    });
  }, [headerLeading, setPageHeaderSlots]);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const requiredFields = form.requiredData?.requiredFields ?? [];

  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ py: 2, maxWidth: PAGE_MAX_WIDTH, width: '100%', mr: 'auto' }}>
      <ProductBasicInfoSection
        productCode={form.productCode}
        productName={form.productName}
        description={form.description}
        price={form.price}
        onChangeName={(v) => updateField('productName', v)}
        onChangeDescription={(v) => updateField('description', v)}
        onChangePrice={(v) => updateField('price', v)}
      />

      <ProductSpecificationSection
        specification={form.specification}
        coverages={form.productCoverages}
        allCountries={allCountries}
        onChangeSpecification={updateSpecification}
        onChangeSpecificationData={updateDataCapacity}
        onAddCoverage={addCoverage}
        onDeleteCoverage={deleteCoverage}
      />

      <ProductSettingsSection
        productStatus={form.productStatus}
        productSaleType={form.productSaleType}
        productType={form.productType}
        requiredFields={requiredFields}
        stockId={form.stockId || null}
        stockOptions={stockOptionList}
        apiParameter={form.apiParameter ?? null}
        isApiInputNeeded={isApiInputNeeded}
        onChangeStatus={(value) => updateField('productStatus', value)}
        onChangeSaleType={(value) => updateField('productSaleType', value)}
        onChangeProductType={(value) => updateField('productType', value)}
        onToggleRequiredField={toggleRequiredField}
        onChangeStockId={(value) => updateField('stockId', value === null ? null : value)}
        onChangeApiParameter={(value) => updateField('apiParameter', value ?? undefined)}
      />

      {/* Footer actions */}
      <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CloseIcon />}
          onClick={handleCancel}
        >
          Cancel
        </Button>

        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
          Save changes
        </Button>
      </Box>

      {/* Danger Zone */}
      <Box sx={{ mt: 3 }}>
        <DangerZoneCard
          priority="danger"
          title="Delete Product"
          description="Permanently removes this product. This action cannot be undone."
          actions={
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={handleDelete}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Delete Product
            </Button>
          }
        />
      </Box>
    </Box>
  );
};

export default ProductDetail;
